import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { DEFAULT_PORTAL_ROOT_ID } from './constants';
import { createPortalRoot } from './helper';

type PortalProps = {
  children: React.ReactNode;
  rootId?: string;
};

const Portal = ({ children, rootId }: PortalProps): React.ReactPortal => {
  const rootRef = useRef<HTMLElement | null>(null);
  const elementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!rootId || rootId === DEFAULT_PORTAL_ROOT_ID) {
      rootRef.current = document.getElementById(DEFAULT_PORTAL_ROOT_ID);
    } else {
      rootRef.current = createPortalRoot(rootId);
    }
  }, [rootId]);

  useEffect(() => {
    if (!rootRef.current || !elementRef.current) {
      throw new Error('Portal has not been initialized.');
    }
    rootRef.current.appendChild(elementRef.current);
    return () => {
      if (elementRef.current) {
        elementRef.current.remove();
      }
      if (
        rootId &&
        rootId !== DEFAULT_PORTAL_ROOT_ID &&
        rootRef.current?.childElementCount === 0
      ) {
        rootRef.current.remove();
      }
    };
  }, [rootId]);

  const element = () => {
    if (!elementRef.current) {
      elementRef.current = document.createElement('div');
    }
    return elementRef.current;
  };

  return createPortal(children, element());
};

export default Portal;
